import React, { useEffect, useState } from "react";
import { api, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend, LineChart, Line, CartesianGrid } from "recharts";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

const COLORS = ["#F59E0B","#059669","#3B82F6","#8B5CF6","#EC4899","#0EA5E9","#EF4444","#64748B"];

export default function Reports() {
  const { user } = useAuth();
  const [source, setSource] = useState([]);
  const [status, setStatus] = useState([]);
  const [monthly, setMonthly] = useState([]);
  const [techPerf, setTechPerf] = useState([]);

  useEffect(()=>{
    api.get("/reports/leads-by-source").then(({data})=>setSource(data));
    api.get("/reports/leads-by-status").then(({data})=>setStatus(data));
    api.get("/reports/monthly-leads").then(({data})=>setMonthly(data));
    if (user.role === "admin") api.get("/reports/technician-performance").then(({data})=>setTechPerf(data));
  },[user.role]);

  const downloadReport = async (kind, fmt = "csv") => {
    try {
      const r = await api.get(`/reports/download/${kind}?fmt=${fmt}`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: fmt === "json" ? "application/json" : "text/csv" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = `${kind}-${new Date().toISOString().slice(0,10)}.${fmt}`;
      link.click();
      toast.success(`${kind} ${fmt.toUpperCase()} download started`);
    } catch (e) {
      toast.error("Download failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-3xl font-extrabold text-slate-900" style={{fontFamily:'Outfit'}}>Reports <span className="text-slate-400 text-lg font-medium">/ रिपोर्ट</span></h1>
          <p className="text-slate-500 text-sm">Analytics across leads, sales & operations. Har data CSV me download ho jayega (Excel me open ho jaata hai).</p>
        </div>
      </div>

      {user.role === "admin" && (
        <Card><CardContent className="p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-3">Download Data (CSV — Excel me open ho jaata hai)</div>
          <div className="space-y-2">
            {[
              ["leads","Leads","bg-blue-50 border-blue-200 text-blue-800 hover:bg-blue-100"],
              ["customers","Customers","bg-emerald-50 border-emerald-200 text-emerald-800 hover:bg-emerald-100"],
              ["quotations","Quotations","bg-amber-50 border-amber-200 text-amber-800 hover:bg-amber-100"],
              ["tax-invoices","Tax Invoices","bg-purple-50 border-purple-200 text-purple-800 hover:bg-purple-100"],
              ["payments","Payments","bg-rose-50 border-rose-200 text-rose-800 hover:bg-rose-100"],
              ["services","Services","bg-slate-50 border-slate-300 text-slate-800 hover:bg-slate-100"],
              ["complaints","Complaints","bg-orange-50 border-orange-200 text-orange-800 hover:bg-orange-100"],
            ].map(([kind,label,cls])=>(
              <div key={kind} className="flex items-center gap-2 flex-wrap">
                <div className="text-xs font-semibold text-slate-700 w-32">{label}</div>
                <Button variant="outline" size="sm" onClick={()=>downloadReport(kind,"csv")} className={cls} data-testid={`download-${kind}`}>
                  <Download className="w-3.5 h-3.5 mr-1"/>CSV (Excel)
                </Button>
              </div>
            ))}
          </div>
        </CardContent></Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <Card><CardContent className="p-6">
          <div className="font-semibold text-slate-900 mb-3" style={{fontFamily:'Outfit'}}>Leads by Source</div>
          <ResponsiveContainer width="100%" height={280}>
            <PieChart>
              <Pie data={source} dataKey="count" nameKey="source" outerRadius={95} label>
                {source.map((_,i)=><Cell key={i} fill={COLORS[i%COLORS.length]}/>)}
              </Pie>
              <Tooltip/><Legend/>
            </PieChart>
          </ResponsiveContainer>
        </CardContent></Card>

        <Card><CardContent className="p-6">
          <div className="font-semibold text-slate-900 mb-3" style={{fontFamily:'Outfit'}}>Leads by Status</div>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={status}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0"/>
              <XAxis dataKey="status" tick={{fontSize:11}}/>
              <YAxis tick={{fontSize:11}}/>
              <Tooltip/>
              <Bar dataKey="count" fill="#F59E0B" radius={[6,6,0,0]}/>
            </BarChart>
          </ResponsiveContainer>
        </CardContent></Card>

        <Card className="lg:col-span-2"><CardContent className="p-6">
          <div className="font-semibold text-slate-900 mb-3" style={{fontFamily:'Outfit'}}>Monthly Leads Trend</div>
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={monthly}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0"/>
              <XAxis dataKey="month" tick={{fontSize:11}}/>
              <YAxis tick={{fontSize:11}}/>
              <Tooltip/>
              <Legend/>
              <Line type="monotone" dataKey="total" stroke="#3B82F6" strokeWidth={2.5} name="Total Leads"/>
              <Line type="monotone" dataKey="won" stroke="#059669" strokeWidth={2.5} name="Won"/>
            </LineChart>
          </ResponsiveContainer>
        </CardContent></Card>

        {user.role === "admin" && techPerf.length > 0 && (
          <Card className="lg:col-span-2"><CardContent className="p-6">
            <div className="font-semibold text-slate-900 mb-3" style={{fontFamily:'Outfit'}}>Technician Performance</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-slate-200">
                  <tr><th className="py-2">Technician</th><th className="py-2">Services</th><th className="py-2">Completed</th><th className="py-2">Complaints</th><th className="py-2">Resolved</th></tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {techPerf.map((t,i)=>(
                    <tr key={i}>
                      <td className="py-2.5 font-medium">{t.name}<div className="text-xs text-slate-500">{t.email}</div></td>
                      <td className="py-2.5">{t.services}</td>
                      <td className="py-2.5 text-emerald-700 font-semibold">{t.services_done}</td>
                      <td className="py-2.5">{t.complaints}</td>
                      <td className="py-2.5 text-emerald-700 font-semibold">{t.complaints_done}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent></Card>
        )}
      </div>
    </div>
  );
}
